import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, Clock, User, MessageSquare, Calendar, AlertCircle, CheckCircle, XCircle, Send, Plus, Edit, Trash2 } from 'lucide-react';
import { Todo, TodoComment } from '../services/todoService';
import { useAuth } from '../contexts/AuthContext';
import { parseDateString } from '../utils/dateUtils';

interface TodoDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  todo: Todo | null;
  onStatusChange?: (todo: Todo, newStatus: string) => void;
  onAddComment?: (todoId: number, comment: string) => Promise<TodoComment | undefined>;
  onEdit?: (todo: Todo) => void;
  onDelete?: (todo: Todo) => void;
}

const TodoDetailsModal: React.FC<TodoDetailsModalProps> = ({
  isOpen,
  onClose,
  todo,
  onStatusChange,
  onAddComment,
  onEdit,
  onDelete
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [newComment, setNewComment] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [localTodo, setLocalTodo] = useState<Todo | null>(todo);

  // Check if current user can edit/delete this todo
  const canEdit = user && (
    user.role === 'admin' ||
    localTodo?.assigned_to === user.id ||
    localTodo?.created_by === user.id ||
    localTodo?.assign_to_all === 1
  );

  const canDelete = user && (
    user.role === 'admin' ||
    localTodo?.assigned_to === user.id ||
    localTodo?.created_by === user.id ||
    localTodo?.assign_to_all === 1
  );

  // Update local todo when prop changes
  useEffect(() => {
    if (todo) {
      // Update if it's a different task
      if (!localTodo || todo.id !== localTodo.id) {
        setLocalTodo(todo);
      } else {
        // Same task - update if comments array has changed
        const todoCommentsCount = todo.comments?.length || 0;
        const localCommentsCount = localTodo.comments?.length || 0;
        
        // Update if comment count changed (new comment added from parent)
        if (todoCommentsCount !== localCommentsCount) {
          setLocalTodo(todo);
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todo]);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'in_progress':
        return 'bg-teal-100 text-teal-800 border-teal-200';
      case 'pending':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4" />;
      case 'in_progress':
        return <Clock className="h-4 w-4" />;
      case 'pending':
        return <AlertCircle className="h-4 w-4" />;
      case 'cancelled':
        return <XCircle className="h-4 w-4" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };

  const formatDate = (dateString: string) => {
    // Use parseDateString to extract date without timezone conversion (same fix as payment)
    const parsedDate = parseDateString(dateString);
    if (!parsedDate) return '';
    
    // Create date from parsed YYYY-MM-DD string to avoid timezone conversion
    const [year, month, day] = parsedDate.split('-').map(Number);
    const dateToFormat = new Date(year, month - 1, day);
    
    const locale = localStorage.getItem('i18nextLng') || 'en';
    return dateToFormat.toLocaleDateString(locale === 'ar' ? 'ar-SA' : locale === 'fr' ? 'fr-FR' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || !localTodo || !onAddComment) return;

    setCommentLoading(true);
    try {
      const newCommentData = await onAddComment(localTodo.id, newComment.trim());
      
      // Update localTodo immediately with the new comment (add at the beginning for newest first)
      if (newCommentData) {
        setLocalTodo({
          ...localTodo,
          comments: [newCommentData, ...(localTodo.comments || [])]
        });
      }
      
      setNewComment('');
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setCommentLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (localTodo && onStatusChange) {
      // Create updated todo for immediate UI update
      const updatedTodo = { ...localTodo, status: newStatus as any };
      
      // Update local state immediately for instant UI update
      setLocalTodo(updatedTodo);
      
      // Call the parent callback to update the database
      onStatusChange(localTodo, newStatus);
    }
  };

  if (!isOpen || !localTodo) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-2 sm:p-3 md:p-4" style={{ zIndex: 99999 }}>
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-md"
        onClick={onClose}
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
      />
      <div className="relative bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start sm:items-center justify-between p-4 sm:p-6 border-b border-gray-100 gap-3">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <div className="p-1.5 sm:p-2 bg-teal-100 rounded-lg flex-shrink-0">
              <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-teal-600" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 break-words">{localTodo.title}</h2>
              <p className="text-xs sm:text-sm text-gray-600">{t('todo.taskDetails', 'Task Details')}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0 touch-manipulation"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          {/* Status and Priority */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-4">
            <span className={`inline-flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium border ${getStatusColor(localTodo.status)}`}>
              {getStatusIcon(localTodo.status)}
              <span className="hidden sm:inline">{t(`todo.status.${localTodo.status}`, localTodo.status.replace('_', ' '))}</span>
              <span className="sm:hidden">{t(`todo.status.${localTodo.status}`, localTodo.status.replace('_', ' ')).substring(0, 1)}</span>
            </span>
            <span className={`inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium border ${getPriorityColor(localTodo.priority)}`}>
              {t(`todo.priority.${localTodo.priority}`, localTodo.priority)}
            </span>
          </div>

          {/* Description */}
          {localTodo.description && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">{t('todo.description', 'Description')}</h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed break-words">{localTodo.description}</p>
            </div>
          )}

          {/* Task Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">{t('todo.assignedTo', 'Assigned To')}</h3>
                <div className="flex items-center gap-2 text-gray-600">
                  <User className="h-4 w-4" />
                  <span>{localTodo.assignee_first_name} {localTodo.assignee_last_name}</span>
                </div>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">{t('todo.createdBy', 'Created By')}</h3>
                <div className="flex items-center gap-2 text-gray-600">
                  <User className="h-4 w-4" />
                  <span>{localTodo.creator_first_name} {localTodo.creator_last_name}</span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {localTodo.due_date && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">{t('todo.dueDate', 'Due Date')}</h3>
                  <div className="flex items-center gap-2 text-gray-600">
                    <Calendar className="h-4 w-4" />
                    <span>{formatDate(localTodo.due_date)}</span>
                  </div>
                </div>
              )}
              
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">{t('todo.created', 'Created')}</h3>
                <div className="flex items-center gap-2 text-gray-600">
                  <Clock className="h-4 w-4" />
                  <span>{formatDate(localTodo.created_at)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Status Actions */}
          <div className="p-3 sm:p-4 bg-gray-50 rounded-xl">
            <h3 className="text-sm font-medium text-gray-700 mb-3">{t('todo.quickActions', 'Quick Actions')}</h3>
            <div className="flex items-center gap-2 flex-wrap">
              {localTodo.status !== 'completed' && (
                <>
                  {localTodo.status === 'pending' && (
                    <button
                      onClick={() => handleStatusChange('in_progress')}
                      className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-teal-600 bg-teal-50 hover:bg-teal-100 rounded-lg transition-colors touch-manipulation"
                    >
                      {t('todo.startTask', 'Start Task')}
                    </button>
                  )}
                  
                  {localTodo.status === 'in_progress' && (
                    <button
                      onClick={() => handleStatusChange('completed')}
                      className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-green-600 bg-green-50 hover:bg-green-100 rounded-lg transition-colors touch-manipulation"
                    >
                      {t('todo.markComplete', 'Mark Complete')}
                    </button>
                  )}
                </>
              )}
              
              {/* Edit and Delete buttons */}
              {canEdit && onEdit && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    // Call onEdit - it will handle closing the details modal
                    onEdit(localTodo!);
                  }}
                  className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-teal-600 bg-teal-50 hover:bg-teal-100 rounded-lg transition-colors flex items-center gap-1 sm:gap-2 touch-manipulation"
                >
                  <Edit className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="hidden sm:inline">{t('todos.editTask', 'Edit Task')}</span>
                  <span className="sm:hidden">{t('todos.editTask', 'Edit Task').substring(0, 4)}</span>
                </button>
              )}
              
              {canDelete && onDelete && (
                <button
                  onClick={() => {
                    onDelete(localTodo!);
                    onClose();
                  }}
                  className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors flex items-center gap-1 sm:gap-2 touch-manipulation"
                >
                  <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="hidden sm:inline">{t('todos.deleteTask', 'Delete Task')}</span>
                  <span className="sm:hidden">{t('todos.deleteTask', 'Delete Task').substring(0, 3)}</span>
                </button>
              )}
            </div>
          </div>

          {/* Comments Section */}
          <div>
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
              <MessageSquare className="h-4 w-4 sm:h-5 sm:w-5" />
              {t('todo.comments', 'Comments')} ({localTodo.comments?.length || 0})
            </h3>

            {/* Add Comment */}
            <form onSubmit={handleAddComment} className="mb-4 sm:mb-6">
              <div className="flex gap-2 sm:gap-3">
                <input
                  type="text"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder={t('todo.addCommentPlaceholder', 'Add a comment...')}
                  className="flex-1 px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                />
                <button
                  type="submit"
                  disabled={!newComment.trim() || commentLoading}
                  className="px-3 sm:px-4 py-2 sm:py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation flex-shrink-0"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </form>

            {/* Comments List */}
            <div className="space-y-3 sm:space-y-4">
              {localTodo.comments && localTodo.comments.length > 0 ? (
                [...localTodo.comments]
                  .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                  .map((comment: TodoComment) => (
                  <div key={comment.id} className="p-3 sm:p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-2 sm:gap-3 mb-2">
                      <div className="w-7 h-7 sm:w-8 sm:h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <User className="h-3 w-3 sm:h-4 sm:w-4 text-teal-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                          {comment.first_name} {comment.last_name}
                        </p>
                        <p className="text-[10px] sm:text-xs text-gray-500">{formatDate(comment.created_at)}</p>
                      </div>
                    </div>
                    <p className="text-sm sm:text-base text-gray-700 break-words">{comment.comment}</p>
                  </div>
                ))
              ) : (
                <div className="text-center py-6 sm:py-8 text-gray-500">
                  <MessageSquare className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-2 sm:mb-3 text-gray-300" />
                  <p className="text-sm sm:text-base">{t('todo.noComments', 'No comments yet. Be the first to add one!')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default TodoDetailsModal;
